// src/hooks/useBalanceSummary.js
//
// The balance summary (credits-api `summary`) for the signed-in account,
// through lib/summaryCache.js: cached and deduplicated 30 s, invalidated on
// money changes (invalidateBalance) and cleared on logout. It reads with the
// EXISTING session token only — it never asks the wallet for a signature
// (hovering the account card must not prompt). No session or a failed read
// → summary null, rendered "—".

import { useCallback, useEffect, useRef, useState } from 'react';
import { createSummaryCache } from '../lib/summaryCache';
import { creditsApi } from '../lib/credits';
import { authService } from '../services/AuthService';
import { useWallet } from '../context/WalletContext';

const cache = createSummaryCache({
  fetch: async (account) => {
    const token = authService.getToken(account);
    if (!token) return null;
    return creditsApi(token, { action: 'summary' });
  },
});
const listeners = new Set();

/** a money change for this account: the next read is fresh, mounted readers refresh */
export function invalidateBalance(account) {
  cache.invalidate(account);
  listeners.forEach((f) => f(account));
}
/** the balance moved (a purchase, a gift, a hold): every cached answer is stale */
export const balanceChanged = () => clearBalanceCache();
export function clearBalanceCache() {
  cache.clear();
  listeners.forEach((f) => f(null));
}

export function useBalanceSummary({ enabled = true } = {}) {
  const { walletAddress } = useWallet();
  const account = walletAddress ? walletAddress.toLowerCase() : null;
  // state is scoped to the account it was read for: another account's answer is never shown
  const [state, setState] = useState(() => ({ account, summary: account ? cache.peek(account) : null }));
  const [loading, setLoading] = useState(false);
  const current = useRef(account);
  current.current = account;

  const load = useCallback(async ({ fresh = false } = {}) => {
    if (!account) { setState({ account: null, summary: null }); return null; }
    setLoading(true);
    const s = await cache.get(account, { fresh });
    if (current.current !== account) return null;            // the wallet switched while reading: drop it
    setState({ account, summary: s });
    setLoading(false);
    return s;
  }, [account]);

  // an identity change clears what is shown at once
  useEffect(() => { setState({ account, summary: account ? cache.peek(account) : null }); }, [account]);
  useEffect(() => { if (enabled) load(); }, [enabled, load]);
  useEffect(() => {
    const f = (acc) => { if (!acc || acc.toLowerCase() === account) load(); };
    listeners.add(f);
    return () => listeners.delete(f);
  }, [load, account]);

  const summary = state.account === account ? state.summary : null;
  return { summary, loading, refresh: () => load({ fresh: true }) };
}
